/**
 * Marketing pricing config — the single source of truth for the public
 * /pricing page (tier cards, the feature-comparison matrix and the FAQ).
 *
 * DISPLAY TEXT IS i18n: every user-facing string in this file is an i18n KEY
 * PATH (e.g. "pricingPage.plans.starter.name") resolved at render time via
 * `t(...)` in the (client) page components. The English/Dutch copy lives in
 * src/i18n/locales/marketing/pricing.ts. Numbers, booleans, hrefs, plan ids,
 * the `recommended` flag, comparison-cell truth values and array order are the
 * real, non-translated data and are kept verbatim here.
 *
 * This is PRICE DISPLAY CONFIG. It deliberately holds no entitlement logic and
 * is never imported by auth, routing or governance code — what a tenant may do
 * comes from the PLAN_* settings a subscription transition provisions
 * (src/server/entitlements/plan-entitlements.ts), never from a price.
 *
 * Since CORE-E3 it IS read by one billing surface: `/api/billing` renders the
 * amounts on /settings/billing from these rows. That is the point rather than
 * a leak — the settings page and the pricing page must never disagree about
 * what a plan costs, and a second copy of the numbers is exactly how they
 * would. Which plans can actually be BOUGHT is a separate question, answered
 * by the configured Stripe price map, not by this file: a row here is a price
 * we advertise, not a promise that checkout can sell it.
 *
 * Keeping the numbers, feature lists and comparison cells here (rather than
 * inlined across components) means a price or limit changes in exactly one
 * place. The page components (`<PricingTiers>`, `<PricingComparison>`) are
 * presentational and read from these structures.
 *
 * Prices are in euros, excluding VAT (EU B2B). Annual prices reflect roughly
 * two months free vs. paying monthly.
 */

export type BillingPeriod = "monthly" | "annual";

export type PlanId = "starter" | "team" | "pro" | "enterprise";

export type PricingPlan = {
  id: PlanId;
  name: string;
  /** One-line positioning shown under the name. */
  positioning: string;
  /** Who the plan is for — rendered as a small "Best for" line. */
  forWho: string;
  /** Monthly price in euros, or null for custom (Enterprise). */
  monthly: number | null;
  /** Annual price in euros/year, or null for custom (Enterprise). */
  annual: number | null;
  /** i18n key for a price prefix, e.g. "pricingPage.tiers.from" for Enterprise. */
  pricePrefix?: string;
  /** Highlight as the recommended tier. */
  recommended?: boolean;
  /** Primary call-to-action for the card. */
  cta: { label: string; href: string };
  /** Small note under the CTA (trial / pilot wording). */
  ctaNote?: string;
  /** Included features, shown as a checklist on the card. */
  features: string[];
  /** Limits, shown as a compact key/value pair. */
  limits: { users: string; aiActions: string };
  /** Plan id this tier inherits from, used for the "Everything in X, plus" lead-in. */
  inheritsFrom?: PlanId;
};

/** Annual prices are ~2 months free; expressed explicitly so the math is auditable. */
export const PRICING_PLANS: readonly PricingPlan[] = [
  {
    id: "starter",
    name: "pricingPage.plans.starter.name",
    positioning: "pricingPage.plans.starter.positioning",
    forWho: "pricingPage.plans.starter.forWho",
    monthly: 99,
    annual: 990,
    cta: { label: "pricingPage.plans.starter.ctaLabel", href: "/signup?plan=starter" },
    ctaNote: "pricingPage.plans.starter.ctaNote",
    limits: { users: "pricingPage.plans.starter.limitsUsers", aiActions: "pricingPage.plans.starter.limitsAiActions" },
    features: [
      "pricingPage.plans.starter.f1",
      "pricingPage.plans.starter.f2",
      "pricingPage.plans.starter.f3",
      "pricingPage.plans.starter.f4",
      "pricingPage.plans.starter.f5",
      "pricingPage.plans.starter.f6",
    ],
  },
  {
    id: "team",
    name: "pricingPage.plans.team.name",
    positioning: "pricingPage.plans.team.positioning",
    forWho: "pricingPage.plans.team.forWho",
    monthly: 299,
    annual: 2990,
    recommended: true,
    cta: { label: "pricingPage.plans.team.ctaLabel", href: "/signup?plan=team" },
    ctaNote: "pricingPage.plans.team.ctaNote",
    limits: { users: "pricingPage.plans.team.limitsUsers", aiActions: "pricingPage.plans.team.limitsAiActions" },
    inheritsFrom: "starter",
    features: [
      "pricingPage.plans.team.f1",
      "pricingPage.plans.team.f2",
      "pricingPage.plans.team.f3",
      "pricingPage.plans.team.f4",
      "pricingPage.plans.team.f5",
      "pricingPage.plans.team.f6",
      "pricingPage.plans.team.f7",
      "pricingPage.plans.team.f8",
    ],
  },
  {
    id: "pro",
    name: "pricingPage.plans.pro.name",
    positioning: "pricingPage.plans.pro.positioning",
    forWho: "pricingPage.plans.pro.forWho",
    monthly: 799,
    annual: 7990,
    cta: { label: "pricingPage.plans.pro.ctaLabel", href: "/demo?plan=pro" },
    ctaNote: "pricingPage.plans.pro.ctaNote",
    limits: { users: "pricingPage.plans.pro.limitsUsers", aiActions: "pricingPage.plans.pro.limitsAiActions" },
    inheritsFrom: "team",
    features: [
      "pricingPage.plans.pro.f1",
      "pricingPage.plans.pro.f2",
      "pricingPage.plans.pro.f3",
      "pricingPage.plans.pro.f4",
      "pricingPage.plans.pro.f5",
      "pricingPage.plans.pro.f6",
      "pricingPage.plans.pro.f7",
    ],
  },
  {
    id: "enterprise",
    name: "pricingPage.plans.enterprise.name",
    positioning: "pricingPage.plans.enterprise.positioning",
    forWho: "pricingPage.plans.enterprise.forWho",
    monthly: 1500,
    annual: null,
    pricePrefix: "pricingPage.tiers.from",
    cta: { label: "pricingPage.plans.enterprise.ctaLabel", href: "/contact?plan=enterprise" },
    ctaNote: "pricingPage.plans.enterprise.ctaNote",
    limits: { users: "pricingPage.plans.enterprise.limitsUsers", aiActions: "pricingPage.plans.enterprise.limitsAiActions" },
    inheritsFrom: "pro",
    features: [
      "pricingPage.plans.enterprise.f1",
      "pricingPage.plans.enterprise.f2",
      "pricingPage.plans.enterprise.f3",
      "pricingPage.plans.enterprise.f4",
      "pricingPage.plans.enterprise.f5",
      "pricingPage.plans.enterprise.f6",
      "pricingPage.plans.enterprise.f7",
    ],
  },
];

/* -------------------------------------------------------------------------- */
/*  FEATURE COMPARISON MATRIX                                                  */
/* -------------------------------------------------------------------------- */

/**
 * A comparison cell. Booleans render as a check / dash; a string renders as a
 * cautious inline note ("where configured", "1 of Freshdesk/Jira", …) and is
 * treated as "included, with a caveat".
 */
export type ComparisonCell = boolean | string;

export type ComparisonRow = {
  label: string;
  /** Optional caveat shown under the label for the whole row. */
  caveat?: string;
  cells: Record<PlanId, ComparisonCell>;
};

export type ComparisonGroup = {
  title: string;
  rows: ComparisonRow[];
};

// Shorthands so the matrix below stays readable.
const Y = true;
const N = false;

export const COMPARISON_GROUPS: readonly ComparisonGroup[] = [
  {
    title: "pricingPage.comparison.groups.intake.title",
    rows: [
      { label: "pricingPage.comparison.rows.websiteChatIntake.label", cells: { starter: Y, team: Y, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.slackIntake.label", cells: { starter: Y, team: Y, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.teamsIntake.label", cells: { starter: N, team: Y, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.ticketQuestionAnswering.label", cells: { starter: Y, team: Y, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.threadAwareFollowups.label", cells: { starter: N, team: Y, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.channelRedaction.label", cells: { starter: Y, team: Y, pro: Y, enterprise: Y } },
    ],
  },
  {
    title: "pricingPage.comparison.groups.helpdesk.title",
    rows: [
      {
        label: "pricingPage.comparison.rows.freshdeskIntegration.label",
        cells: { starter: "pricingPage.cells.oneOfFreshdeskJira", team: Y, pro: Y, enterprise: Y },
      },
      {
        label: "pricingPage.comparison.rows.jiraIntegration.label",
        cells: { starter: "pricingPage.cells.oneOfFreshdeskJira", team: Y, pro: Y, enterprise: Y },
      },
      {
        label: "pricingPage.comparison.rows.odooIntegration.label",
        cells: { starter: "pricingPage.cells.oneOfFreshdeskJira", team: Y, pro: Y, enterprise: Y },
      },
      {
        label: "pricingPage.comparison.rows.topdeskIntegration.label",
        cells: { starter: "pricingPage.cells.oneOfFreshdeskJira", team: Y, pro: Y, enterprise: Y },
      },
      { label: "pricingPage.comparison.rows.aiProposedReplies.label", cells: { starter: Y, team: Y, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.humanEditBeforeSend.label", cells: { starter: Y, team: Y, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.freshdeskCustomerReply.label", cells: { starter: N, team: Y, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.odooCustomerReply.label", cells: { starter: N, team: Y, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.topdeskCustomerReply.label", cells: { starter: N, team: Y, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.jiraInternalComment.label", cells: { starter: N, team: Y, pro: Y, enterprise: Y } },
      {
        label: "pricingPage.comparison.rows.jsmCustomerReply.label",
        caveat: "pricingPage.comparison.rows.jsmCustomerReply.caveat",
        cells: { starter: N, team: "pricingPage.cells.whereSupported", pro: "pricingPage.cells.whereSupported", enterprise: "pricingPage.cells.whereSupported" },
      },
      { label: "pricingPage.comparison.rows.ticketWriteOutbox.label", cells: { starter: N, team: Y, pro: Y, enterprise: Y } },
    ],
  },
  {
    title: "pricingPage.comparison.groups.governance.title",
    rows: [
      { label: "pricingPage.comparison.rows.approvalLedger.label", cells: { starter: Y, team: Y, pro: Y, enterprise: Y } },
      {
        label: "pricingPage.comparison.rows.twoFactor.label",
        caveat: "pricingPage.comparison.rows.twoFactor.caveat",
        cells: { starter: Y, team: Y, pro: Y, enterprise: Y },
      },
      { label: "pricingPage.comparison.rows.actorRoleChannel.label", cells: { starter: Y, team: Y, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.operationalTimeline.label", cells: { starter: "pricingPage.cells.basic", team: Y, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.capabilityHealth.label", cells: { starter: Y, team: Y, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.outboxRetryDlq.label", cells: { starter: N, team: Y, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.auditExport.label", cells: { starter: N, team: N, pro: Y, enterprise: Y } },
    ],
  },
  {
    title: "pricingPage.comparison.groups.engineering.title",
    rows: [
      { label: "pricingPage.comparison.rows.githubGitlab.label", cells: { starter: N, team: N, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.codebaseAnalysis.label", cells: { starter: N, team: N, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.patchDiffEvidence.label", cells: { starter: N, team: N, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.testsQaSecurity.label", cells: { starter: N, team: N, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.prMrWorkflow.label", cells: { starter: N, team: N, pro: Y, enterprise: Y } },
      { label: "pricingPage.comparison.rows.riskGates.label", cells: { starter: N, team: N, pro: Y, enterprise: Y } },
    ],
  },
  {
    title: "pricingPage.comparison.groups.deployment.title",
    rows: [
      {
        label: "pricingPage.comparison.rows.argoVisibility.label",
        cells: { starter: N, team: N, pro: "pricingPage.cells.whereConfigured", enterprise: Y },
      },
      {
        label: "pricingPage.comparison.rows.deploymentApproval.label",
        cells: { starter: N, team: N, pro: "pricingPage.cells.whereConfigured", enterprise: Y },
      },
      {
        label: "pricingPage.comparison.rows.promoteAbortRead.label",
        caveat: "pricingPage.comparison.rows.promoteAbortRead.caveat",
        cells: { starter: N, team: N, pro: "pricingPage.cells.whereConfigured", enterprise: Y },
      },
      { label: "pricingPage.comparison.rows.failureVisibility.label", cells: { starter: N, team: N, pro: "pricingPage.cells.whereConfigured", enterprise: Y } },
    ],
  },
  {
    title: "pricingPage.comparison.groups.enterprise.title",
    rows: [
      {
        label: "pricingPage.comparison.rows.sso.label",
        caveat: "pricingPage.comparison.rows.sso.caveat",
        cells: { starter: N, team: N, pro: N, enterprise: "pricingPage.cells.whereImplemented" },
      },
      {
        label: "pricingPage.comparison.rows.scim.label",
        caveat: "pricingPage.comparison.rows.scim.caveat",
        cells: { starter: N, team: N, pro: N, enterprise: "pricingPage.cells.whereImplemented" },
      },
      { label: "pricingPage.comparison.rows.customPolicies.label", cells: { starter: N, team: N, pro: N, enterprise: Y } },
      {
        label: "pricingPage.comparison.rows.privateDeployment.label",
        caveat: "pricingPage.comparison.rows.privateDeployment.caveat",
        cells: { starter: N, team: N, pro: N, enterprise: "pricingPage.cells.whereSupported" },
      },
      { label: "pricingPage.comparison.rows.slaSecurityReview.label", cells: { starter: N, team: N, pro: N, enterprise: Y } },
    ],
  },
];

/* -------------------------------------------------------------------------- */
/*  FAQ                                                                        */
/* -------------------------------------------------------------------------- */

export const PRICING_FAQ: readonly { q: string; a: string }[] = [
  { q: "pricingPage.faq.q1.q", a: "pricingPage.faq.q1.a" },
  { q: "pricingPage.faq.q2.q", a: "pricingPage.faq.q2.a" },
  { q: "pricingPage.faq.q3.q", a: "pricingPage.faq.q3.a" },
  { q: "pricingPage.faq.q4.q", a: "pricingPage.faq.q4.a" },
  { q: "pricingPage.faq.q5.q", a: "pricingPage.faq.q5.a" },
  { q: "pricingPage.faq.q6.q", a: "pricingPage.faq.q6.a" },
  { q: "pricingPage.faq.q7.q", a: "pricingPage.faq.q7.a" },
  { q: "pricingPage.faq.q8.q", a: "pricingPage.faq.q8.a" },
  { q: "pricingPage.faq.q9.q", a: "pricingPage.faq.q9.a" },
  { q: "pricingPage.faq.q10.q", a: "pricingPage.faq.q10.a" },
  { q: "pricingPage.faq.q11.q", a: "pricingPage.faq.q11.a" },
  { q: "pricingPage.faq.q12.q", a: "pricingPage.faq.q12.a" },
  { q: "pricingPage.faq.q13.q", a: "pricingPage.faq.q13.a" },
  { q: "pricingPage.faq.q14.q", a: "pricingPage.faq.q14.a" },
  { q: "pricingPage.faq.q15.q", a: "pricingPage.faq.q15.a" },
  { q: "pricingPage.faq.q16.q", a: "pricingPage.faq.q16.a" },
  { q: "pricingPage.faq.q17.q", a: "pricingPage.faq.q17.a" },
];

/** Format a euro amount with no decimals and EU thousands separators. */
export function formatEuro(amount: number): string {
  return `€${amount.toLocaleString("en-IE")}`;
}
